import Cookies from 'js-cookie';
import PubSub from 'pubsub-js';
import client from '../client';
import { CART } from './queries';
import { PRODUCT_VARIANT } from '../products/queries';
import {
  CART_CREATE,
  CART_DISCOUNT_CODE_UPDATE,
  CART_LINES_ADD,
  CART_LINES_REMOVE,
  CART_LINES_UPDATE,
  CART_ATTRIBUTES_UPDATE,
  CART_NOTE_UPDATE,
  UPDATE_CART_COUNTRY
} from './mutations';
import {
  getCookieDomain,
  transformTagsIntoProperties,
  insertIf
} from '../../../util/util';
import { getUserCountry } from '../../../util/localize';

import { trackEvent as trackGTM } from '../../../util/track';
import { QBS_ENABLED } from '../../../util/const';
import { PRODUCT_VARIANT_COUNT } from '../../../components/Navigation/Navigation.gql';
import { NOTE } from '../../../components/Checkout/components/GiftingContainer/GiftingContainer.gql';
import {
  CURRENT_USER,
  DISCOUNT_ALLOCATIONS,
  GET_USER_LOCALE
} from '../../../components/App.gql';
import { checkIsAttributable } from '../../../util/rebuyAttribution';
import { track } from '../../../util/optimizely';

export const CART_ID_COOKIE = 'BOM-CART_ID';

/**
 * Publishes a cartUpdate event and passes through the result; for use as a `then` handler.
 * @param {*} result Proxied result
 * @return {*} result
 */
export const proxyPublishCartUpdate = (result) => {
  PubSub.publish('cart.update');
  return result;
};

/**
 * Resets the customers cart by removing the cookie and optionally triggering a UI refresh.
 */
export const resetCart = (skipUpdate = false) => {
  Cookies.remove(CART_ID_COOKIE, { domain: getCookieDomain() });
  if (!skipUpdate) proxyPublishCartUpdate();
};

/**
 * Converts a Shopify ProductVariant ID to a namespaced global ID.
 * @param {Number|String} variantId ProductVariant ID
 * @returns {String} Namespaced Global ID
 */
export const convertVariantIdToGlobalId = (variantId) =>
  `gid://shopify/ProductVariant/${variantId}`;

/**
 * Converts a Shopify namespaced Global ID to a raw ID.
 * @param {String} globalId Namespaced Global ID
 * @returns {String} ID
 */
export const convertGlobalIdToRawId = (globalId) =>
  globalId.substr(globalId.lastIndexOf('/') + 1);

/**
 * Creates a Storefront request handler with a root accessor.
 * @deprecated This approach should not be necessary for @apollo implementation. Handle specific storefront errors with an Error link in `/graphql/client.js'. https://www.apollographql.com/docs/react/api/link/apollo-link-error/
 * @param {String} key Root key accessor in expected response
 * @returns {Promise} Resolves with response in key, rejects with error.
 */
export const handleStorefrontRequest =
  (key, attr = {}) =>
  (result) => {
    const data = result?.[key];

    const customAttributes = { location: 'services/storefront' };

    if (key === 'cart' && data === null) {
      resetCart();

      const cartError = new Error(
        'Unknown cart error. Storefront returned null when fetching for Cart information using BOM-CART_ID.'
      );
      window.DD_RUM?.addError(cartError, { ...attr, ...customAttributes });
      return Promise.reject(cartError);
    }

    if (data?.userErrors?.length) {
      const error = new Error(JSON.stringify(data.userErrors));

      window.DD_RUM?.addError(error, { ...attr, ...customAttributes });
      return Promise.reject(error);
    }

    if (data?.cart) return Promise.resolve(data.cart);

    if (data) return Promise.resolve(data);

    const genericError = new Error(
      `An unknown error has occurred making a request to Storefront; key = ${key}.`
    );
    window.DD_RUM?.addError(genericError, { ...attr, ...customAttributes });
    return Promise.reject(genericError);
  };

/**
 * Retrieve ProductVariant attr and push event to Google Tag Manager
 * @param {Object} event Object with event and variant fields
 */
export const trackEvent = async ({ event, variant }) => {
  try {
    const variantData = await client
      .request(PRODUCT_VARIANT, {
        productVariantId: convertVariantIdToGlobalId(variant.id)
      })
      .then(handleStorefrontRequest('node', { variantId: variant.id }));

    const product = {
      json: {
        id: convertGlobalIdToRawId(variantData.product.id),
        title: variantData.product.title,
        price: parseFloat(variantData.priceV2.amount, 10) * 100,
        type: variantData.product.productType,
        pdpLink: variant.pdpLink
      }
    };

    /**
     * Clear any preexisting variant/product objects
     * @see https://developers.google.com/analytics/devguides/collection/ua/gtm/enhanced-ecommerce#clear-ecommerce
     */
    trackGTM({ ecommerce: null });
    trackGTM({
      event,
      variant,
      product,
      ecommerce: {
        items: [
          {
            item_id: convertGlobalIdToRawId(variantData.product.id),
            item_name: variantData.product.title,
            affiliation: 'Bombas',
            currency: variantData.priceV2.currencyCode,
            item_category: variantData.product.productType,
            item_category2: transformTagsIntoProperties(
              variantData.product.tags
            ).gender?.[0],
            item_brand: 'Bombas',
            item_variant: variantData.title,
            price: parseFloat(variantData.priceV2.amount, 10),
            quantity: variant.quantity
          }
        ]
      }
    });
  } catch (e) {
    window.DD_RUM?.addError(e, { location: 'services/storefront/trackEvent' });
  }
};

/**
 * Gets the value of the BOM-CART_ID cookie, refreshes its expiration date (+14 days); optionally creates a cart ID if it doesn't exist.
 * @param apolloClient apolloClient instance
 * @param {Boolean} create If true, creates a cart id cookie if it doesnt exist.
 * @param {Boolean} ignore If true, ignores any existing cart id.
 * @returns {Promise} Resolves with cart ID (or false if create = false), rejects if error trying to create cart ID.
 */
export const getCartId = async (
  apolloClient,
  create = true,
  ignore = false
) => {
  const params = new URLSearchParams(window.location.search);
  const cartIdParam = params.get('cartId');

  const cartId = cartIdParam || Cookies.get(CART_ID_COOKIE);

  if (cartId && !ignore) {
    // Extend cookie expiration 14 days from now.
    Cookies.set(CART_ID_COOKIE, cartId, {
      domain: getCookieDomain(),
      expires: 14
    });

    return cartId;
  }

  if (create) {
    const landingSiteUrl = Cookies.get('BOM-LANDING_SITE');
    const mutationVariables = {
      input: {
        attributes: [
          ...insertIf(landingSiteUrl, [
            {
              key: 'landing_site',
              value: landingSiteUrl
            }
          ]),
          {
            key: '_disableDiscount',
            value: QBS_ENABLED ? 'false' : 'true'
          }
        ]
      }
    };

    const { user } = apolloClient.readQuery({
      query: GET_USER_LOCALE
    });
    const userCountry = user?.locale?.country;

    if (userCountry) {
      mutationVariables.input.buyerIdentity = {
        countryCode: userCountry
      };
    }

    const result = await client
      .request(CART_CREATE, {
        ...mutationVariables,
        country: userCountry
      })
      .then(handleStorefrontRequest('cartCreate'))
      .catch((e) => {
        window.DD_RUM?.addError(e, { location: 'services/storefront' });
        return Promise.reject(new Error(e));
      });

    if (result?.id) {
      const { id } = result;
      Cookies.set(CART_ID_COOKIE, id, {
        domain: getCookieDomain(),
        expires: 14
      });
      return id;
    }

    return Promise.reject(new Error('Missing cart id.'));
  }

  return false;
};

export const updateCartCountry = async (country) => {
  const params = new URLSearchParams(window.location.search);
  const cartIdParam = params.get('cartId');

  const cartId = cartIdParam || Cookies.get(CART_ID_COOKIE);

  const results = await client
    .request(UPDATE_CART_COUNTRY, {
      buyerIdentity: {
        countryCode: country
      },
      cartId
    })
    .catch((e) => {
      window.DD_RUM?.addError(e, { location: 'services/storefront' });
    });

  const cartBuyerIdentityUpdate = results?.cartBuyerIdentityUpdate;

  if (cartBuyerIdentityUpdate?.userErrors.length) {
    const { message } = cartBuyerIdentityUpdate.userErrors[0];
    window.DD_RUM?.addError(new Error(message), {
      location: 'services/storefront'
    });
  }
};

/**
 * Performs a single field GraphQL query and automatically retries once with a new cart ID if the
 * original request has an invalid cart ID or has already been completed.
 * @param  {...any} params Parameters forwarded to `client.request`
 * @param apolloClient apollo client
 * @returns {Promise} Result
 */
export const safeRequest = (doc, vars, apolloClient) =>
  client
    .request(doc, vars)
    .then(async (result) => {
      const data = result?.[Object.keys(result)?.[0]];

      if (data?.userErrors?.length) {
        const error = new Error(
          JSON.stringify({
            originalRequest: { doc, vars },
            userErrors: data.userErrors
          })
        );

        // Returns a new cart when Storefront throws an INVALID cart ID error
        if (
          data.userErrors.some(
            (cartError) =>
              cartError.code === 'INVALID' && cartError.field.includes('cartId')
          )
        ) {
          window.DD_RUM?.onReady(() => {
            window.DD_RUM?.addError(error, {
              location: 'services/storefront'
            });
          });
          const cartId = await getCartId(apolloClient, true, true);

          return client.request(doc, { ...vars, cartId });
        }

        const products = vars?.lines;

        window.DD_RUM?.onReady(() => {
          window.DD_RUM?.addError(error, {
            location: 'services/storefront',
            products
          });
        });
      }

      return result;
    })
    .catch((error) => {
      // log server errors from storefront api (timeouts, throttles, internal errors, etc)
      window.DD_RUM?.onReady(() => {
        window.DD_RUM?.addError(error, { location: 'services/storefront' });
      });

      return Promise.reject(error);
    });

/**
 * Retrieves the contents of the cart.
 * @returns {Promise} Resolves with cart contents or null; rejects if cart error.
 */
export const getCart = async (apolloClient) => {
  // Removes old Checkout ID value in BOM-CART_ID so that it can be replaced with the Cart ID
  if (Cookies.get(CART_ID_COOKIE)?.includes('Checkout')) {
    resetCart();
  }

  const cartId = await getCartId(apolloClient);

  apolloClient.writeQuery({
    query: CURRENT_USER,
    data: {
      cartId
    }
  });

  if (cartId) {
    const country = getUserCountry(apolloClient);
    return safeRequest(CART, { cartId, country }, apolloClient)
      .then(handleStorefrontRequest('cart'))
      .then((res) => {
        // write line item quantities to apollo cache to update Navigation cart item count
        apolloClient.writeQuery({
          query: PRODUCT_VARIANT_COUNT,
          data: {
            cart: {
              __typename: 'Cart',
              id: res.id,
              lines: res.lines
            }
          },
          variables: {
            cartId
          }
        });

        return Promise.resolve(res);
      });
  }

  return null;
};

/**
 * Add a product variant to cart.
 * @param {Number} variantId ProductVariant identifier
 * @param {Number} quantity ProductVariant quantity
 * @param {string[]} category Product category/categories
 * @param {Object} pimData Product PIM data
 * @param apolloClient apollo client
 * @returns {Promise} Resolves with updated Cart contents; rejects if Cart error.
 */
export const addToCart = async (
  variantId,
  quantity,
  sku,
  pdpLink = null,
  pimData,
  customAttributes = [],
  apolloClient,
  productId
) => {
  const cartId = await getCartId(apolloClient);
  const attributionSource = checkIsAttributable(productId);

  customAttributes.push({
    key: '_pimData',
    value: JSON.stringify({
      variantId,
      ...pimData
    })
  });

  if (attributionSource) {
    track(
      'add_to_cart_from_rebuy_upsell',
      {},
      { $opt_event_properties: { sourceId: attributionSource } }
    );
    customAttributes.push(
      { key: '_source', value: 'Rebuy' },
      { key: '_attribution', value: attributionSource }
    );
  }

  trackEvent({
    event: 'addToCart',
    variant: { id: variantId, quantity, sku, customAttributes, pdpLink }
  });

  const country = getUserCountry(apolloClient);

  return safeRequest(
    CART_LINES_ADD,
    {
      cartId,
      lines: [
        {
          merchandiseId: convertVariantIdToGlobalId(variantId),
          quantity,
          attributes: customAttributes
        }
      ],
      country
    },
    apolloClient
  )
    .then(proxyPublishCartUpdate)
    .then(handleStorefrontRequest('cartLinesAdd', { variantId }));
};

/**
 * Add a multiple variant IDs to a cart.
 * The sum of ocurrences of a given variant ID is used to indicate quantity.
 * @param apolloClient apolloClient
 * @param {...Number} variantId ProductVariant identifier
 * @returns {Promise} Resolves with updated Cart contents; rejects if Cart error.
 */
export const bulkAddToCart = async (apolloClient, ...variantIds) => {
  if (!variantIds) return;

  const cartId = await getCartId(apolloClient);
  const lineItems = variantIds.reduce((result, variantId) => {
    result[variantId] = (result[variantId] || 0) + 1; // eslint-disable-line no-param-reassign
    return result;
  }, {});
  const country = getUserCountry(apolloClient);

  return safeRequest(
    CART_LINES_ADD,
    {
      cartId,
      lines: Object.keys(lineItems).map((id) => ({
        merchandiseId: convertVariantIdToGlobalId(id),
        quantity: lineItems[id]
      })),
      country
    },
    apolloClient
  )
    .then(proxyPublishCartUpdate)
    .then(handleStorefrontRequest('cartLinesAdd', { variantIds }));
};

/**
 * Remove line item(s) from cart.
 * @param apolloClient apollo client
 * @param  {...String} lines Base64 encoded namespaced global ID(s) to remove
 * @returns {Promise} Resolves with updated Cart contents; rejects if Cart error.
 */
export const removeFromCart = async (apolloClient, ...lines) => {
  const cartId = await getCartId(apolloClient, false);

  if (cartId) {
    const country = getUserCountry(apolloClient);
    return safeRequest(
      CART_LINES_REMOVE,
      {
        cartId,
        lines,
        country
      },
      apolloClient
    )
      .then(proxyPublishCartUpdate)
      .then(handleStorefrontRequest('cartLinesRemove', { lines }));
  }

  return Promise.reject(new Error('Missing cart ID.'));
};

/**
 * Update line item quantity from cart.
 * @param {String} lineItemId Base64 encoded namespaced global ID to update
 * @param {Number} quantity New quantity
 * @param {Number} variantId Base64 encoded namespaced variant ID
 * @param {Number} oldQuantity previous quantity
 * @param apolloClient apollo client
 * @returns {Promise} Resolves with updated Cart contents; rejects if Cart error.
 */
export const updateItemQuantity = async (
  lineItemId,
  quantity,
  variantId,
  oldQuantity,
  apolloClient
) => {
  const cartId = await getCartId(apolloClient, false);

  if (quantity === 0) {
    trackEvent({
      event: 'removeFromCart',
      variant: {
        id: convertGlobalIdToRawId(variantId),
        quantity: oldQuantity
      }
    });
  }

  if (cartId) {
    const country = getUserCountry(apolloClient);
    return safeRequest(
      CART_LINES_UPDATE,
      {
        cartId,
        lines: [{ id: lineItemId, merchandiseId: variantId, quantity }],
        country
      },
      apolloClient
    )
      .then(proxyPublishCartUpdate)
      .then(handleStorefrontRequest('cartLinesUpdate', { variantId }));
  }

  return Promise.reject(new Error('Missing cart ID.'));
};

/**
 * Updates Apollo cache after a discount is removed or added.
 * The cache is used in DiscountAppliedMessage, CouponDialog, and Footer.
 *
 * @param {Object} res GQL Response returned from cartDiscountCodeUpdate mutation
 * @param {String} cartId Shopify Cart ID
 * @param {} apolloClient Apollo Client
 */
export const updateCachedDiscount = (res, cartId, apolloClient) => {
  const { discountAllocations, discountCodes } = res;

  // write updated discounts to apollo cache to update Footer
  apolloClient.writeQuery({
    query: CURRENT_USER,
    data: {
      cartId
    }
  });

  apolloClient.writeQuery({
    query: DISCOUNT_ALLOCATIONS,
    data: {
      cart: {
        __typename: 'Cart',
        id: res.id,
        discountAllocations,
        discountCodes
      }
    },
    variables: {
      cartId
    }
  });
};

/**
 * Apply a discount code to a new or existing cart.
 * @param {String[]} discountCodes Array of discount codes to pass into CartDiscountCodesUpdate mutation
 * @returns {Promise} Resolves with updated Cart contents; rejects if Cart error / invalid code.
 */
export const applyDiscountCode = async (discountCodes, apolloClient) => {
  const cartId = await getCartId(apolloClient);
  const country = getUserCountry(apolloClient);

  return safeRequest(
    CART_DISCOUNT_CODE_UPDATE,
    {
      cartId,
      discountCodes,
      country
    },
    apolloClient
  )
    .then(handleStorefrontRequest('cartDiscountCodesUpdate'))
    .then((res) => {
      trackGTM({
        event: 'cartDiscountCode',
        // Pass in a single applied discount code to match previous Checkout implementation
        ecommerce: { discount_code: discountCodes[0] }
      });
      updateCachedDiscount(res, cartId, apolloClient);
      return res;
    });
};

/**
 * There is no specific query to strictly remove a discount code.
 * Therefore, we must pass in an empty array in the discountCode argument to mimic the removal of discount codes.
 */
export const removeDiscountCode = async (apolloClient) => {
  const cartId = await getCartId(apolloClient);
  // Empty array to mock no discounts/discount removal
  const discountCodes = [];
  const country = getUserCountry(apolloClient);

  return safeRequest(
    CART_DISCOUNT_CODE_UPDATE,
    {
      cartId,
      discountCodes,
      country
    },
    apolloClient
  )
    .then(handleStorefrontRequest('cartDiscountCodesUpdate'))
    .then((res) => {
      updateCachedDiscount(res, cartId, apolloClient);
      return res;
    })
    .catch((e) => e);
};

/**
 * Set a gift message on an existing cart.
 * @param {String} note Gift message
 * @returns {Promise} Resolves with updated Cart contents; rejects if Cart error.
 */
export const setGiftMessage = async (note, apolloClient) => {
  const cartId = await getCartId(apolloClient);
  const country = getUserCountry(apolloClient);

  return safeRequest(
    CART_NOTE_UPDATE,
    {
      cartId,
      note,
      country
    },
    apolloClient
  )
    .then(handleStorefrontRequest('cartNoteUpdate'))
    .then(proxyPublishCartUpdate)
    .then((result) => {
      apolloClient.writeQuery({
        query: NOTE,
        data: {
          cart: {
            id: cartId,
            note,
            __typename: 'Cart'
          }
        }
      });

      return result;
    })
    .catch((e) => e);
};

/**
 *  Updates the Cart object with an array of new attributes and merges them with existing attributes.
 *
 * @param {Object[]} newAttributes array of attributes to be added to the cart object
 * @param {String} newAttributes.key identifier for the custom attribute
 * @param {String} newAttributes.value value of the custom attribute
 * @param apolloClient apollo client
 */
export const setCustomAttributes = async (newAttributes, apolloClient) => {
  const cartId = await getCartId(apolloClient);
  const country = getUserCountry(apolloClient);

  const cart = await safeRequest(CART, { cartId, country }).then(
    handleStorefrontRequest('cart')
  );

  const existingAttributes = cart.attributes || [];

  const mergedAttributes = [
    ...existingAttributes.filter(
      (existing) =>
        !newAttributes.some((newAttr) => newAttr.key === existing.key)
    ),
    ...newAttributes
  ];

  return safeRequest(
    CART_ATTRIBUTES_UPDATE,
    {
      cartId,
      attributes: mergedAttributes,
      country
    },
    apolloClient
  )
    .then(handleStorefrontRequest('cartAttributesUpdate'))
    .then(proxyPublishCartUpdate);
};
